import {action, computed, observable, runInAction} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import unionBy from 'lodash/unionBy';
import appStore from '../../app/store';
import mainStore from '../../../containers/main/store';
import {GROUP} from '../../../constants/urlConstants';
import request from '../../../utils/request';
import {go} from '../../../utils/navigationUtility';
import manageStore from '../../../stores/ManageStore';
import {uploadToS3} from '../../../utils/awsUtility';
import {guid} from "../../../utils/commonUtility";
import {newTopic} from "../../../constants/constants";

class GroupStore {
    @observable groupsList = [];
    @observable groupLessons = [];
    @observable isGroupLessonsLoaded = false;
    @observable activeGroupID = null;
    @observable editableMode = false;
    @observable isFetchingGroups = false;
    @observable isProcessing = false;
    @observable showSkip = false;
    @observable searchStr = '';
    @observable isGroupBackgroundEditing = false;
    @observable isSavingBackground = false;
    @observable editedGroup = null;

    @computed get groups() {
        return this.groupsList.sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
    }

    @computed get lessons() {
        const sortedLessons = this.groupLessons.sort((a, b) => {
            return Date.parse(b.created) - Date.parse(a.created);
        });
        return sortedLessons.filter((group) => {
            return (group.name.toLowerCase().includes(this.searchStr));
        }).map((lesson, index) => {
            return {
                id: guid(),
                created: new Date().toDateString(),
                topic: newTopic(),
                note: null,
                lessons: [{...lesson, show: true}],
                order: index,
                editMode: false,
                isTemp: true
            }
        });
    }

    @computed get activeGroup() {
        const group = this.groupsList.find(group => group.id === this.activeGroupID);
        if (group) {
            const filteredStudentsList = (group.students || []).filter(student => !!manageStore.getStudent(student.id));
            return {...group, students: filteredStudentsList};
        }
        return {};
    }

    @action setActiveGroupID = (val) => {
        this.activeGroupID = val;
    };

    @action clearActiveGroupID = () => {
        this.activeGroupID = null;
    };

    @action setSearchStr = (val) => {
        this.searchStr = val;
    };

    @action setEditableMode = (val) => {
        this.editableMode = val;
    };

    @action setShowSkip = (val) => {
        this.showSkip = val;
    };

    @action fetchGroupList = async (force = false) => {
        if ((this.isFetchingGroups || !isEmpty(this.groupsList)) && !force) {
            return;
        }
        this.isFetchingGroups = true;
        try {
            const groupsList = await request(GROUP);
            runInAction(() => {
                this.groupsList = unionBy(this.groupsList, groupsList, 'id');
            });
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isFetchingGroups = false;
            });
        }
    };

    @action addGroup = async (groupName, teacherId) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const query = teacherId ? `?teacherId=${teacherId}` : '';
            const group = await request(`${GROUP}${query}`, {method: 'POST', body: {name: groupName}});
            runInAction(() => {
                this.groupsList.push(group);
            });
            this.setActiveGroupID(group.id);
            appStore.createAlertFromMessageId('success', 'addGroup.wasAdded', 2000);
        } catch (err) {
            throw err;
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action editGroup = async (id, name, students) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const newGroup = await request(`${GROUP}/${id}`, {method: 'POST', body: {name, students}});
            this.replaceGroup(newGroup);
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action updateGroupBackground = async (file, group) => {
        this.editedGroup = group;
        await this.saveGroupBackground(file, true);
        runInAction(() => {
            this.editedGroup = null;
        });
    };

    @action resetGroupBackground = async () => {
        mainStore.toggleProgressLoader(true);
        const group = this.activeGroup;
        try {
            await request(`${GROUP}/${group.id}`, {method: 'POST', body: {backgroundImg: ''}});
            runInAction(() => {
                group.backgroundImg = '';
                if (this.editedGroup) {
                    this.editedGroup.backgroundImg = '';
                }
            });
        } catch (err) {
            console.warn(err.message);
            // appStore.createAlertFromMessageId('error', 'notifications.saveError');
        } finally {
            runInAction(() => {
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action saveGroupBackground = async (file, isEdit) => {
        if (!isEdit) {
            this.closeGroupBackgroundEditing();
            return;
        }
        this.isSavingBackground = true;
        const id = `${this.activeGroupID}_${Date.now()}`;
        mainStore.toggleProgressLoader(true);
        this.closeGroupBackgroundEditing();
        try {
            const group = this.activeGroup;
            await uploadToS3(file, id, file.type, null, 'musico-public-bucket/media/group', false);
            runInAction(() => {
                group.backgroundImg = `/media/group/${id}`;
                if (this.editedGroup) {
                    this.editedGroup.backgroundImg = `/media/group/${id}`;
                }
            });
            await request(`${GROUP}/${group.id}`, {method: 'POST', body: {backgroundImg: group.backgroundImg}});
        } catch (err) {
            appStore.createAlertFromMessageId('error', 'notifications.saveError');
        } finally {
            runInAction(() => {
                this.isSavingBackground = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action closeGroupBackgroundEditing = () => {
        this.isGroupBackgroundEditing = false;
        this.editedGroup = null;
    };

    @action openGroupBackgroundEditing = () => {
        this.isGroupBackgroundEditing = true;
        this.editedGroup = this.activeGroup;
    };

    @action removeGroup = async (id) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            await request(`${GROUP}/${id}`, {method: 'DELETE'});
            this.removeGroupByID(id);
            go('/home/select');
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action replaceGroup = (newGroup) => {
        if (!isEmpty(newGroup.students)) {
            newGroup.students = newGroup.students.map(student => {
                const exStudent = manageStore.studentsList.find(s => student.id === s.id) || {};
                return {...student, status: exStudent.status};
            });
        }
        const groupIndex = this.groupsList.findIndex(group => group.id === newGroup.id);
        const mergedGroup = {...this.groupsList[groupIndex], ...newGroup};
        this.groupsList.splice(groupIndex, 1, mergedGroup);
    };

    @action removeGroupByID = (id) => {
        const groupIndex = this.groupsList.findIndex(group => group.id === id);
        this.groupsList.splice(groupIndex, 1);
    };

    @action fetchGroup = async (id) => {
        this.isProcessing = true;
        this.isGroupLessonsLoaded = false;
        try {
            this.setActiveGroupID(id || this.activeGroupID);
            const group = await request(`${GROUP}/${id || this.activeGroupID}`);
            this.replaceGroup(group);
            runInAction(() => {
                this.groupLessons = group.lessons;
            });
        } catch (err) {
            console.warn(err.message);
            go('/home/select');
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                this.isGroupLessonsLoaded = true;
            });
        }
    };

    getGroupsByStudentId = (id) => {
        return this.groupsList.reduce((list, group) => {
            if (group.students && group.students.some(student => student.id === id)) {
                list.push({name: group.name, id: group.id});
            }
            return list;
        }, []);
    };

    getGroupNameById = (id) => {
        return (this.groupsList.find(group => group.id === id) || {}).name;
    };

    isGroupNameExists = groupName => (
        this.groupsList.some(g => g.name === groupName)
    );

    getGroupStudents = (groupId) => {
        const index = this.groupsList.findIndex(group => group.id === groupId);
        let result =  index !== -1 ? this.groupsList[index].students || [] : [];
        return result.filter(student => !!manageStore.getStudent(student.id))
    };

    getGroupName = (groupId) => {
        const index = this.groupsList.findIndex(group => group.id === groupId);
        return index !== -1 ? this.groupsList[index].name : '';
    }
}

const groupStore = new GroupStore();
export default groupStore;
